import { Loader2 } from "lucide-react";
import * as React from "react";
import { cn } from "../../lib/utils";

const VARIANT_CLASS: Record<string, string> = {
  default:
    "bg-primary text-primary-foreground hover:bg-primary/90",
  outline:
    "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
  ghost:
    "hover:bg-accent hover:text-accent-foreground",
  secondary:
    "bg-secondary text-secondary-foreground hover:bg-secondary/80",
  destructive:
    "text-destructive-foreground hover:bg-destructive/10 hover:text-destructive border-0",
  "ghost-destructive":
    "text-muted-foreground hover:text-destructive hover:bg-destructive/10",
};

export interface ExpandableIconButtonProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  href?: string;
  onClick?: (e: React.MouseEvent) => void;
  variant?: keyof typeof VARIANT_CLASS;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  iconClassName?: string;
  "aria-label"?: string;
}

export function ExpandableIconButton({
  icon: Icon,
  label,
  href,
  onClick,
  variant = "outline",
  className,
  disabled,
  loading,
  iconClassName,
  "aria-label": ariaLabel,
}: ExpandableIconButtonProps) {
  const cls = cn(
    "group inline-flex h-8 shrink-0 items-center justify-center rounded-md px-2",
    "text-sm font-medium ring-offset-background transition-colors",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "disabled:pointer-events-none disabled:opacity-50",
    VARIANT_CLASS[variant],
    className
  );

  const inner = loading ? (
    <Loader2 className={cn("h-3.5 w-3.5 animate-spin shrink-0", iconClassName)} />
  ) : (
    <>
      <Icon className={cn("h-3.5 w-3.5 shrink-0", iconClassName)} />
      <span
        className={cn(
          "ml-0 max-w-0 overflow-hidden whitespace-nowrap opacity-0 text-xs",
          "transition-all duration-200",
          "group-hover:ml-1.5 group-hover:max-w-[80px] group-hover:opacity-100"
        )}
      >
        {label}
      </span>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={ariaLabel ?? label}
        className={cls}
      >
        {inner}
      </a>
    );
  }

  return (
    <button
      type="button"
      aria-label={ariaLabel ?? label}
      className={cls}
      onClick={onClick}
      disabled={disabled || loading}
    >
      {inner}
    </button>
  );
}
